import React, { useMemo, useState } from 'react';
import { NSButton } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { AttributeKey } from 'views/Reports/types';
import { useGetAttributes } from 'components/DealForm/hooks/useGetAttributes';
import { IFilter } from './types';
import FilterModal from './FilterModal/FilterModal';
import Filter from './Filter';
import { Attributes } from '../constants';
import { unitTypeSelectorOptions } from './constants';

interface IFilterSectionProps {
    className?: string;
    filters: IFilter[];
    setFilters: (filters: IFilter[]) => void;
}

const FiltersSection = ({ className, filters, setFilters }: IFilterSectionProps) => {
    const [isFilterModalShown, setIsFilterModalShown] = useState<boolean>(false);

    const { data: dealAttributes } = useGetAttributes();

    const attributeOptions: IOptionAnyValue[] = useMemo(
        () => dealAttributes?.map(
            attribute => {
                const isSelected = filters.some(f => f.attribute === attribute.name && attribute.name !== Attributes.Units);
                return {
                    label: attribute.name,
                    value: attribute.correlationId,
                    disabled: isSelected,
                    isChecked: isSelected,
                };
            },
        ) ?? [], [dealAttributes, filters],
    );

    const customAttributes = useMemo(
        () => dealAttributes?.filter(attribute => attribute.isCustom),
        [dealAttributes],
    );

    const unitTypeOptions: IOptionAnyValue[] = useMemo(() => unitTypeSelectorOptions.map(option => {
        const isSelected = filters.some(
            f => f.unitType === option.value && f.attribute === Attributes.Units,
        );
        return {
            label: option.label,
            value: option.value,
            disabled: isSelected,
            isChecked: isSelected,
        };
    }), [filters]);

    const toggleFilterModal = () => setIsFilterModalShown(!isFilterModalShown);

    const addFilter = (filter: IFilter) => {
        setFilters([...filters, filter]);
    };

    const removeFilter = (attribute: AttributeKey, unitType?: string) => {
        const newFilters = filters.filter(f => {
            const isAttributeMatch = f.attribute === attribute;
            const isUnitTypeMatch = unitType ? f.unitType === unitType : false;
            return !(isAttributeMatch && (!unitType || isUnitTypeMatch));
        });
        setFilters(newFilters);
    };

    const editFilter = (attribute: AttributeKey, filter: IFilter, unitType?: string) => {
        const updatedFilters = filters.map(f => {
            const isAttributeMatch = f.attribute === attribute;
            const isUnitTypeMatch = unitType ? f.unitType === unitType : false;

            if (isAttributeMatch && (!unitType || isUnitTypeMatch)) {
                return filter;
            }

            return f;
        });
        setFilters(updatedFilters);
    };

    return (
        <div className={className}>
            <div>
                {filters.map(filter => (
                    <Filter
                        key={filter.attribute}
                        filter={filter}
                        editFilter={editFilter}
                        removeFilter={removeFilter}
                        attributeOptions={attributeOptions}
                        customAttributes={customAttributes}
                        unitTypeOptions={unitTypeOptions}
                    />
                ))}
                <NSButton className="text-primary cursor--pointer m-0 mt-1 p-0" callback={() => setIsFilterModalShown(true)}>
                    + Add filter
                </NSButton>
            </div>
            <FilterModal
                toggle={toggleFilterModal}
                isOpen={isFilterModalShown}
                saveFilter={addFilter}
                attributeOptions={attributeOptions}
                customAttributes={customAttributes}
                unitTypeOptions={unitTypeOptions}
            />
        </div>
    );
};

export default FiltersSection;
